// Web Speech API wrapper for kid voice input in Learn Mode.
// Kids in K1–K2 often can't type fluently; tap-to-speak is the realistic input.
// Usage:
//   const { available, listening, start, stop, transcript, error } = useSTT();
//   <button onMouseDown={start} onMouseUp={stop}>🎙</button>

import { useCallback, useEffect, useRef, useState } from 'react';

type SpeechRecognitionEventLike = {
  results: ArrayLike<{
    isFinal: boolean;
    0: { transcript: string };
    length: number;
  }>;
};

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function useSTT() {
  const Ctor = getRecognitionCtor();
  const available = !!Ctor;
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionLike | null>(null);

  const start = useCallback(() => {
    if (!Ctor) return;
    setTranscript('');
    setError(null);
    const r = new Ctor();
    r.lang = 'de-DE';
    r.interimResults = true;
    r.continuous = false;
    r.onresult = (e) => {
      let finalText = '';
      let interimText = '';
      for (let i = 0; i < e.results.length; i++) {
        const alt = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += alt;
        else interimText += alt;
      }
      setTranscript((prev) => finalText || interimText || prev);
    };
    r.onerror = (e) => {
      setError(e.error || 'Sprach-Fehler');
      setListening(false);
    };
    r.onend = () => setListening(false);
    recRef.current = r;
    try {
      r.start();
      setListening(true);
    } catch {
      setError('Mikrofon konnte nicht gestartet werden.');
    }
  }, [Ctor]);

  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch { /* ignore */ }
    setListening(false);
  }, []);

  const reset = useCallback(() => {
    setTranscript('');
    setError(null);
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { available, listening, transcript, error, start, stop, reset };
}
